import { FC } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper';
import 'swiper/css';

import { LangSwitcher, SvgIcon } from '@/components';
import useDevice from '@/hooks/useDevice';

import amlbotLogoWhite from '@/assets/images/logo-amlbot-white.svg';
import amlbotLogoBlack from '@/assets/images/logo-amlbot.svg';
import slide1 from '@/assets/images/auth/slide-1.png';

import styles from './AuthLayout.module.sass';

const swiperProps = {
  modules: [Navigation, Pagination, Autoplay],
  spaceBetween: 20,
  loop: true,
  centeredSlides: true,
  slidesPerView: 1,
  autoplay: {
    delay: 6000,
  },
  navigation: {
    prevEl: '.swiper-prev',
    nextEl: '.swiper-next',
  },
  pagination: {
    el: '.swiper-pagination',
    bulletClass: `swiper-pagination-bullet ${styles.bullet}`,
    bulletActiveClass: `swiper-pagination-bullet-active ${styles['is-active']}`,
    clickable: true,
  },
};

const slides = [
  {
    image: slide1,
    title: 'Checking wallets for illicit funds',
    description: 'Be safe with US. We take care of your crypto',
  },
];

interface AuthLayoutProps {}

const Slide: FC<{ image: string; title: string; description: string }> = ({
  image,
  title,
  description,
}) => {
  return (
    <div className={styles.slide}>
      <img src={image} alt="" className={styles.slide__image} />
      <h3 className={styles.slide__title}>{title}</h3>
      <p className={styles.slide__description}>{description}</p>
    </div>
  );
};

const AuthLayout: FC<AuthLayoutProps> = () => {
  const { isMobile } = useDevice();

  const isShowSliderControls = slides.length > 1;

  const logoImg = isMobile ? amlbotLogoBlack : amlbotLogoWhite;

  return (
    <div className={styles.auth}>
      <div className={styles.inner}>
        <Link to="/">
          <img src={logoImg} alt="" className={styles.logo} />
        </Link>

        <div className={styles.info}>
          <div className={styles.slider}>
            <Swiper {...swiperProps}>
              {slides.map((slide) => {
                return (
                  <SwiperSlide key={slide.title}>
                    <Slide
                      image={slide.image}
                      title={slide.title}
                      description={slide.description}
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>

            {isShowSliderControls && (
              <>
                <button
                  type="button"
                  className={`swiper-prev ${styles.arrow} ${styles['arrow--prev']}`}
                >
                  <SvgIcon name="arrow-left-short" />
                </button>

                <button
                  type="button"
                  className={`swiper-next ${styles.arrow} ${styles['arrow--next']}`}
                >
                  <SvgIcon name="arrow-right-short" />
                </button>

                <div className={`swiper-pagination ${styles.pagination}`} />
              </>
            )}
          </div>
        </div>

        <div className={styles.body}>
          <LangSwitcher className={styles.lang} fullLabel={!isMobile} />

          <div className={styles.outlet}>
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
